import { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowRight, Eye, EyeOff, LockKeyhole } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import type { z } from 'zod';
import { loginSchema } from '@shared/schemas';
import { useAuth } from '@/app/auth-context';
import { PageLoading } from '@/components/feedback/page-loading';
import { Button, FieldError, Input, Label } from '@/components/ui';
import { getErrorMessage } from '@/lib/api';
import { routes, safeReturnTo } from '@/lib/routes';
import { usePageMeta } from '@/lib/seo';

type LoginInput = z.input<typeof loginSchema>;
type LoginOutput = z.output<typeof loginSchema>;

export function LoginPage() {
  const { t } = useTranslation(['auth', 'common']);
  const { login, isAuthenticated, isLoading, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = safeReturnTo(searchParams.get('returnTo'));
  const [showPassword, setShowPassword] = useState(false);
  usePageMeta({
    title: t('common:seo.loginTitle'),
    description: t('auth:login.subtitle'),
    noIndex: true,
  });
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
  } = useForm<LoginInput, unknown, LoginOutput>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  if (isLoading) return <PageLoading />;
  if (isAuthenticated) {
    return (
      <Navigate
        to={
          user?.onboardingCompleted
            ? returnTo
            : `${routes.onboarding}?returnTo=${encodeURIComponent(returnTo)}`
        }
        replace
      />
    );
  }

  const onSubmit = async (values: LoginOutput) => {
    try {
      const loggedInUser = await login(values);
      void navigate(
        safeReturnTo(returnTo, loggedInUser.onboardingCompleted ? routes.app : routes.onboarding),
        { replace: true },
      );
    } catch (error) {
      setError('root', { message: getErrorMessage(error, t('auth:login.error')) });
    }
  };

  return (
    <div className="auth-card">
      <p className="eyebrow">{t('auth:login.eyebrow')}</p>
      <h1>{t('auth:login.title')}</h1>
      <p className="auth-card__subtitle">{t('auth:login.subtitle')}</p>
      <form onSubmit={(event) => void handleSubmit(onSubmit)(event)} noValidate>
        <div className="field-group">
          <Label htmlFor="login-email">{t('auth:common.email')}</Label>
          <Input
            id="login-email"
            type="email"
            autoComplete="email"
            invalid={Boolean(errors.email)}
            aria-describedby={errors.email ? 'login-email-error' : undefined}
            {...register('email')}
          />
          <FieldError id="login-email-error">
            {errors.email ? t('auth:validation.email') : undefined}
          </FieldError>
        </div>
        <div className="field-group">
          <Label htmlFor="login-password">{t('auth:common.password')}</Label>
          <div className="password-field">
            <Input
              id="login-password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              invalid={Boolean(errors.password)}
              aria-describedby={errors.password ? 'login-password-error' : undefined}
              {...register('password')}
            />
            <button
              type="button"
              className="password-toggle focus-ring"
              onClick={() => setShowPassword((value) => !value)}
              aria-label={
                showPassword ? t('auth:common.hidePassword') : t('auth:common.showPassword')
              }
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          <FieldError id="login-password-error">
            {errors.password ? t('auth:validation.required') : undefined}
          </FieldError>
        </div>
        {errors.root?.message && (
          <div className="form-alert" role="alert">
            {errors.root.message}
          </div>
        )}
        <Button type="submit" size="lg" className="auth-submit" disabled={isSubmitting}>
          {isSubmitting ? t('auth:login.submitting') : t('auth:login.submit')}{' '}
          {!isSubmitting && <ArrowRight className="rtl-flip" size={18} />}
        </Button>
      </form>
      <p className="auth-switch">
        {t('auth:login.newHere')}{' '}
        <Link to={`${routes.register}?returnTo=${encodeURIComponent(returnTo)}`}>
          {t('auth:login.create')}
        </Link>
      </p>
      <p className="auth-security">
        <LockKeyhole size={15} /> {t('auth:common.secure')}
      </p>
    </div>
  );
}

export default LoginPage;
